// Codec analysis for Ogg files: Opus, Vorbis and FLAC-in-Ogg.
//
// ParsedFile.frames are the packet pieces of the first audio stream. A packet
// can span pages, so we first put the pieces back together into packets. For
// Opus we read the TOC byte and the frame lengths; for Vorbis the block size
// and window flags. For FLAC we reuse the full FLAC decoder.
import { opusConfig, vorbisModeFlags } from '../parsers/ogg';
import type { ParsedFile } from '../parsers/types';
import { flacAnalysis, streamInfoFrom } from './flac';
import type { BarsView, CodecAnalysis, CodecLane, CodecView } from './types';

// ---------------------------------------------------------------------------
// Packets.

interface Packet {
  /** File byte ranges of the pieces, in order. */
  pieces: [number, number][];
  /** The page with the start of this packet is missing. */
  lost: boolean;
  /** The packet never ended (cut off, or a later page is missing). */
  open: boolean;
}

/** Rebuilds the packets of every logical stream from the parser's pages. */
function packetsBySerial(pf: ParsedFile, b: Uint8Array): Map<number, Packet[]> {
  const out = new Map<number, Packet[]>();
  const openBy = new Map<number, Packet>();
  for (const pg of pf.segments) {
    if (pg.type !== 'ogg.page') continue;
    const p = pg.start;
    const nseg = b[p + 26];
    const serial = (b[p + 14] | (b[p + 15] << 8) | (b[p + 16] << 16) | (b[p + 17] << 24)) >>> 0;
    const flags = b[p + 5];
    if (flags & 2) openBy.delete(serial); // a new stream with this serial
    let list = out.get(serial);
    if (!list) out.set(serial, (list = []));
    let s = p + 27 + nseg, len = 0, first = true;
    const piece = (e: number, complete: boolean) => {
      let pk = openBy.get(serial);
      if (!(first && flags & 1 && pk)) {
        if (pk) openBy.delete(serial); // stays open: it never ended
        pk = { pieces: [], lost: first && (flags & 1) === 1, open: true };
        list!.push(pk);
      }
      pk.pieces.push([s, Math.min(e, pg.end)]);
      if (complete) { pk.open = false; openBy.delete(serial); } else openBy.set(serial, pk);
      first = false;
    };
    for (let i = 0; i < nseg; i++) {
      const v = b[p + 27 + i];
      len += v;
      if (v < 255) {
        piece(s + len, s + len <= pg.end);
        s += len;
        len = 0;
      }
    }
    if (len > 0) piece(s + len, false);
  }
  return out;
}

function packetBytes(b: Uint8Array, pk: Packet): Uint8Array {
  if (pk.pieces.length === 1) return b.subarray(pk.pieces[0][0], pk.pieces[0][1]);
  const n = pk.pieces.reduce((a, [s, e]) => a + e - s, 0);
  const out = new Uint8Array(n);
  let o = 0;
  for (const [s, e] of pk.pieces) { out.set(b.subarray(s, e), o); o += e - s; }
  return out;
}

interface Audio {
  /** Audio packets that hold at least one frame, in order. */
  packets: Packet[];
  /** Header packets of the stream (all packets before the first audio one). */
  headers: Packet[];
  /** frames[i] → index into packets. */
  unit: Int32Array;
  /** Time span per packet. */
  times: [number, number][];
}

function collect(pf: ParsedFile, b: Uint8Array): Audio | null {
  const frames = pf.frames;
  if (!frames.length) return null;
  const all = packetsBySerial(pf, b);
  const byStart = new Map<number, { list: Packet[]; k: number }>();
  for (const list of all.values()) list.forEach((pk, k) => pk.pieces.forEach(([s]) => byStart.set(s, { list, k })));
  const first = byStart.get(frames[0].start);
  if (!first) return null;
  const list = first.list;
  const packets: Packet[] = [];
  const times: [number, number][] = [];
  const unit = new Int32Array(frames.length).fill(-1);
  let lastK = -1;
  frames.forEach((f, i) => {
    const hit = byStart.get(f.start);
    if (!hit || hit.list !== list) return;
    const t = f.time ?? [0, 0];
    if (hit.k !== lastK) {
      packets.push(list[hit.k]);
      times.push([t[0], t[1]]);
      lastK = hit.k;
    } else times[times.length - 1][1] = t[1];
    unit[i] = packets.length - 1;
  });
  return { packets, headers: list.slice(0, first.k), unit, times };
}

// ---------------------------------------------------------------------------
// Shared helpers.

const unitAt = (a: Audio, i: number) => (i >= 0 && i < a.unit.length ? a.unit[i] : -1);

const num = (v: number) => v.toLocaleString('en-US');
const pct = (v: number) => `${Math.round(v * 100)}%`;
const plural = (n: number, w: string) => `${num(n)} ${w}${n === 1 ? '' : 's'}`;

/** "CELT 80%, Hybrid 20%" from counts per lane value. */
function shares(lane: CodecLane, counts: number[], lower = false): string {
  const total = counts.reduce((a, c) => a + c, 0) || 1;
  return counts.map((c, k) => [c, k]).filter(([c]) => c > 0).sort((a, b) => b[0] - a[0])
    .map(([c, k]) => `${lower ? lane.values[k].value.toLowerCase() : lane.values[k].value} ${pct(c / total)}`).join(', ');
}

// ---------------------------------------------------------------------------
// Opus.

const OPUS_MODE: CodecLane = {
  name: 'Mode',
  help: 'Opus holds two coders. SILK models the human voice, so it suits speech at low bitrates. CELT is an MDCT transform coder for music and low delay. Hybrid uses both at once.',
  values: [
    { value: 'SILK', help: 'Speech coder (from Skype). It codes the voice as a filter plus an excitation signal.' },
    { value: 'Hybrid', help: 'SILK codes the sound below 8 kHz and CELT codes the sound above it.' },
    { value: 'CELT', help: 'Transform coder: an MDCT splits the sound into frequency bands, like MP3 or AAC but with very low delay.' },
  ],
};
const OPUS_BW: CodecLane = {
  name: 'Bandwidth',
  help: 'The highest frequency that the packet keeps. At low bitrates the encoder narrows it, to spend the bits where the ear notices them most.',
  values: [
    { value: 'Narrow 4 kHz', help: 'Narrowband: up to 4 kHz, like an old phone line.' },
    { value: 'Medium 6 kHz', help: 'Mediumband: up to 6 kHz.' },
    { value: 'Wide 8 kHz', help: 'Wideband: up to 8 kHz. Speech sounds clear.' },
    { value: 'Super-wide 12 kHz', help: 'Super-wideband: up to 12 kHz.' },
    { value: 'Full 20 kHz', help: 'Fullband: up to 20 kHz, the whole range of human hearing.' },
  ],
};
const OPUS_SIZE: CodecLane = {
  name: 'Frame size',
  help: 'How much time one Opus frame codes. Long frames save bits. Short frames lower the delay and follow fast changes.',
  values: ['2.5', '5', '10', '20', '40', '60'].map(v => ({
    value: `${v} ms`,
    help: v === '2.5' || v === '5' ? 'Very short frames, for the lowest delay (CELT only).'
      : v === '10' || v === '20' ? (v === '20' ? 'The usual frame size: a good balance of delay and bits.' : 'Short frames for low delay.')
      : 'Long frames that save bits (SILK only).',
  })),
};

const BW_INDEX: Record<string, number> = { NB: 0, MB: 1, WB: 2, SWB: 3, FB: 4 };
const SIZE_INDEX: Record<number, number> = { 120: 0, 240: 1, 480: 2, 960: 3, 1920: 4, 2880: 5 };
const OPUS_DECISIONS = Array.from({ length: 32 }, (_, c) => {
  const cfg = opusConfig(c);
  return [cfg.mode === 'SILK-only' ? 0 : cfg.mode === 'Hybrid' ? 1 : 2, BW_INDEX[cfg.bwShort], SIZE_INDEX[cfg.samples]];
});

interface OpusFraming { code: number; sizes: number[]; padding: number; vbr: boolean; overhead: number; error?: string }

/** Splits an Opus packet into its frames (RFC 6716, section 3.2). */
export function opusFraming(p: Uint8Array): OpusFraming {
  const N = p.length;
  const code = p[0] & 3;
  const res: OpusFraming = { code, sizes: [], padding: 0, vbr: false, overhead: 1 };
  const size = (at: number): [number, number] => {
    if (at >= N) throw new RangeError('A frame length runs past the end of the packet.');
    if (p[at] < 252) return [p[at], 1];
    if (at + 1 >= N) throw new RangeError('A frame length runs past the end of the packet.');
    return [p[at] + 4 * p[at + 1], 2];
  };
  try {
    if (code === 0) res.sizes = [N - 1];
    else if (code === 1) {
      if ((N - 1) % 2) throw new RangeError('Code 1 needs two frames of equal size, but the byte count is odd.');
      res.sizes = [(N - 1) / 2, (N - 1) / 2];
    } else if (code === 2) {
      const [l, n] = size(1);
      const rest = N - 1 - n - l;
      if (rest < 0) throw new RangeError('The first frame is longer than the packet.');
      res.sizes = [l, rest];
      res.overhead = 1 + n;
    } else {
      if (N < 2) throw new RangeError('Code 3 needs a frame count byte.');
      const b1 = p[1];
      res.vbr = (b1 & 0x80) !== 0;
      const M = b1 & 0x3f;
      if (M === 0) throw new RangeError('The frame count is 0.');
      let at = 2;
      if (b1 & 0x40) {
        let v: number;
        do {
          if (at >= N) throw new RangeError('The padding length runs past the end of the packet.');
          v = p[at++];
          res.padding += v === 255 ? 254 : v;
        } while (v === 255);
      }
      if (res.vbr) {
        let sum = 0;
        for (let k = 0; k < M - 1; k++) {
          const [l, n] = size(at);
          at += n;
          res.sizes.push(l);
          sum += l;
        }
        const last = N - at - res.padding - sum;
        if (last < 0) throw new RangeError('The frame lengths add up to more than the packet.');
        res.sizes.push(last);
      } else {
        const body = N - at - res.padding;
        if (body < 0 || body % M) throw new RangeError(`The ${M} frames of equal size do not fit the packet.`);
        for (let k = 0; k < M; k++) res.sizes.push(body / M);
      }
      res.overhead = at;
    }
  } catch (e) {
    res.error = e instanceof Error ? e.message : String(e);
  }
  return res;
}

const MODE_WHY = [
  'SILK models the human voice, so the encoder picks it for speech at low bitrates.',
  'Hybrid mode codes speech with SILK below 8 kHz and adds CELT above it, for clear speech with a full top end.',
  'CELT is the music coder. The encoder picks it for music, for high bitrates, and for very low delay.',
];
const OPUS_MODE_NAME = ['SILK', 'Hybrid', 'CELT'];
const BW_NAME = ['narrowband (up to 4 kHz)', 'mediumband (up to 6 kHz)', 'wideband (up to 8 kHz)', 'super-wideband (up to 12 kHz)', 'fullband (up to 20 kHz)'];

function opusAnalysis(b: Uint8Array, a: Audio): CodecAnalysis {
  const toc = (u: number) => (a.packets[u].lost ? -1 : b[a.packets[u].pieces[0][0]]);
  const decisions = (i: number): number[] => {
    const u = unitAt(a, i);
    const t = u < 0 ? -1 : toc(u);
    return t < 0 ? [-1, -1, -1] : OPUS_DECISIONS[t >> 3];
  };
  const detail = (i: number): CodecView[] => {
    const u = unitAt(a, i);
    if (u < 0 || a.packets[u].lost) throw new Error('The start of this packet is missing, so its TOC byte is unknown.');
    const p = packetBytes(b, a.packets[u]);
    const cfgNo = p[0] >> 3;
    const cfg = opusConfig(cfgNo);
    const [mode, bw] = OPUS_DECISIONS[cfgNo];
    const stereo = (p[0] & 4) !== 0;
    const fr = opusFraming(p);
    const ms = cfg.samples / 48;
    const count = fr.sizes.length;
    const dur = ms * count;
    const codeText = [
      'code 0: one frame fills the packet',
      'code 1: two frames of the same size',
      'code 2: two frames, and the size of the first one is stored',
      `code 3: ${count} frames, ${fr.vbr ? 'each with its own stored size' : 'all the same size'}${fr.padding ? `, then ${plural(fr.padding, 'byte')} of padding` : ''}`,
    ][fr.code];
    let body = `The TOC byte gives config **${cfgNo}**: **${OPUS_MODE_NAME[mode]}** mode, **${BW_NAME[bw]}**, **${+ms.toFixed(1)} ms** frames, **${stereo ? 'stereo' : 'mono'}**.\n\n`;
    body += `${MODE_WHY[mode]} `;
    body += bw < 4 ? 'The encoder cut the bandwidth to save bits, because the ear notices the highest frequencies least.' : 'The packet keeps the full range that people can hear.';
    body += `\n\nFrame count ${codeText}. `;
    if (fr.error) body += `The framing is damaged: ${fr.error}`;
    else body += `So the packet holds **${plural(count, 'frame')}** (${+dur.toFixed(1)} ms of sound) in ${plural(p.length, 'byte')}, about ${num(Math.round((p.length * 8) / dur))} kbps.`;
    if (a.packets[u].open) body += ' The packet is cut off, so its last frame may be incomplete.';
    body += '\n\nThis app reads only the packet framing: it does not decode the SILK or CELT data inside the frames.';
    const views: CodecView[] = [{ kind: 'text', title: 'What this packet chose', body }];
    if (!fr.error) {
      const bars: BarsView = {
        kind: 'bars',
        title: 'Bytes per Opus frame',
        caption: `How the ${plural(p.length, 'byte')} of the packet split between its frames. The TOC byte and length fields take ${plural(fr.overhead, 'byte')}.${count > 1 && fr.vbr ? ' Frames with more going on get more bytes.' : ''}`,
        unit: 'bytes',
        items: fr.sizes.map((v, k) => ({ label: `Frame ${k + 1}`, value: v, note: v <= 2 ? 'Nearly empty: silence or no change' : undefined })),
      };
      if (fr.padding) bars.items.push({ label: 'Padding', value: fr.padding, note: 'Filler bytes, for example to keep the bitrate constant' });
      views.push(bars);
    }
    return views;
  };
  const summarize = () => {
    const counts = [new Array(3).fill(0), new Array(5).fill(0), new Array(6).fill(0)];
    let n = 0;
    for (let u = 0; u < a.packets.length; u++) {
      const t = toc(u);
      if (t < 0) continue;
      const [t0, t1] = a.times[u];
      const w = Math.max(t1 - t0, 1e-6);
      OPUS_DECISIONS[t >> 3].forEach((v, k) => (counts[k][v] += w));
      n++;
    }
    if (!n) return [];
    const notes: { level: 'info' | 'warn'; text: string }[] = [
      { level: 'info', text: `Opus mode by time: ${shares(OPUS_MODE, counts[0])}. SILK is for speech, CELT for music.` },
      { level: 'info', text: `Bandwidth by time: ${shares(OPUS_BW, counts[1])}.` },
      { level: 'info', text: `Frame size by time: ${shares(OPUS_SIZE, counts[2])}.` },
    ];
    if (counts[0].filter(c => c > 0).length > 1) notes.push({ level: 'info', text: 'The encoder switches mode during the file, to suit the sound: speech-like parts and music-like parts get different coders.' });
    return notes;
  };
  return { codec: 'Opus', lanes: [OPUS_MODE, OPUS_BW, OPUS_SIZE], decisions, detail, summarize };
}

// ---------------------------------------------------------------------------
// Vorbis.

const VORBIS_BLOCK: CodecLane = {
  name: 'Block',
  help: 'Vorbis codes the sound in overlapping blocks of two sizes. Long blocks give fine frequency detail for steady sound. Short blocks follow sharp attacks, so noise does not smear before a drum hit (pre-echo).',
  values: [
    { value: 'Short', help: 'A short block (often 256 samples), for sharp attacks and fast changes.' },
    { value: 'Long', help: 'A long block (often 2048 samples), for steady sound. It gives fine frequency detail.' },
  ],
};

interface VorbisSetup { flags: number[]; modeBits: number; sizes: [number, number] }

function vorbisSetup(b: Uint8Array, a: Audio): VorbisSetup | null {
  if (a.headers.length < 3) return null;
  const id = packetBytes(b, a.headers[0]);
  const setup = packetBytes(b, a.headers[2]);
  if (id.length < 30 || setup.length < 8) return null;
  const flags = vorbisModeFlags(setup);
  if (!flags || !flags.length) return null;
  return { flags, modeBits: 32 - Math.clz32(flags.length - 1), sizes: [1 << (id[28] & 15), 1 << (id[28] >> 4)] };
}

/** Reads the start of a Vorbis audio packet (LSB first). */
function vorbisHead(p: Uint8Array, vs: VorbisSetup): { type: number; mode: number; long: boolean; prev?: number; next?: number } | null {
  let pos = 0;
  const bit = () => { const v = (p[pos >> 3] >> (pos & 7)) & 1; pos++; return v; };
  if (p.length * 8 < 1 + vs.modeBits) return null;
  const type = bit();
  if (type) return { type, mode: -1, long: false };
  let mode = 0;
  for (let k = 0; k < vs.modeBits; k++) mode |= bit() << k;
  const flag = vs.flags[mode];
  if (flag === undefined) return null;
  if (!flag) return { type, mode, long: false };
  if (pos + 2 > p.length * 8) return { type, mode, long: true };
  return { type, mode, long: true, prev: bit(), next: bit() };
}

function vorbisAnalysis(b: Uint8Array, a: Audio, vs: VorbisSetup | null): CodecAnalysis {
  const head = (u: number) => {
    const pk = a.packets[u];
    if (!vs || pk.lost) return null;
    const [s, e] = pk.pieces[0];
    return vorbisHead(b.subarray(s, Math.min(e, s + 2)), vs);
  };
  const decisions = (i: number): number[] => {
    const u = unitAt(a, i);
    const h = u < 0 ? null : head(u);
    return [h && !h.type ? (h.long ? 1 : 0) : -1];
  };
  const detail = (i: number): CodecView[] => {
    const u = unitAt(a, i);
    if (u < 0 || a.packets[u].lost) throw new Error('The start of this packet is missing, so its mode is unknown.');
    if (!vs) throw new Error('The Vorbis setup header could not be read, so the modes are unknown.');
    const h = head(u);
    const len = a.packets[u].pieces.reduce((acc, [s, e]) => acc + e - s, 0);
    if (!h) throw new Error('The mode number of this packet is not valid.');
    if (h.type) return [{ kind: 'text', title: 'Not an audio packet', body: 'The lowest bit of the first byte is 1, so this is not an audio packet. Only header packets have it set.' }];
    const size = vs.sizes[h.long ? 1 : 0];
    let body = `This is an **audio packet** (type bit 0) in **mode ${h.mode}**, so it codes a **${h.long ? 'long' : 'short'} block** of ${num(size)} samples. `;
    body += h.long
      ? 'The encoder uses long blocks for steady sound, because they give fine frequency detail.'
      : 'The encoder switched to a short block because the sound changes fast here, for example at a drum hit. A long block would smear noise before the attack (pre-echo).';
    if (h.long && h.prev !== undefined) {
      const side = (f: number | undefined) => (f ? 'long' : 'short');
      body += `\n\nWindow flags: the block before is **${side(h.prev)}** and the block after is **${side(h.next)}**. `;
      body += h.prev && h.next
        ? 'Both neighbors are long, so the window has gentle slopes on both sides.'
        : 'Next to a short block, the window has a steep slope on that side, so the two blocks still overlap cleanly.';
    }
    body += `\n\nThe packet is ${plural(len, 'byte')}. Each block overlaps its neighbors, and the overlaps add up to smooth sound.`;
    body += '\n\nThis app reads only the packet header: it does not decode the Vorbis floor and residue, so it cannot show the spectrum.';
    return [{ kind: 'text', title: 'What this packet chose', body }];
  };
  const summarize = () => {
    const counts = [0, 0];
    for (let u = 0; u < a.packets.length; u++) {
      const h = head(u);
      if (h && !h.type) counts[h.long ? 1 : 0]++;
    }
    const total = counts[0] + counts[1];
    if (!total) return [];
    const notes: { level: 'info' | 'warn'; text: string }[] = [
      { level: 'info', text: `Block sizes: ${shares(VORBIS_BLOCK, counts, true)} of ${plural(total, 'audio packet')}. ${vs!.sizes[0] === vs!.sizes[1]
        ? `Both block sizes are ${num(vs!.sizes[0])} samples, so this encoder cannot switch to shorter blocks at attacks.`
        : `Short blocks are ${num(vs!.sizes[0])} samples and long blocks are ${num(vs!.sizes[1])}.`}` },
    ];
    if (vs!.sizes[0] !== vs!.sizes[1]) notes.push(counts[0]
      ? { level: 'info', text: 'The encoder switches to short blocks at sharp attacks, to keep noise from spreading before them.' }
      : { level: 'info', text: 'Every packet uses a long block: the sound has no attacks sharp enough to need short blocks.' });
    return notes;
  };
  return { codec: 'Vorbis', lanes: [VORBIS_BLOCK], decisions, detail, summarize };
}

// ---------------------------------------------------------------------------
// Entry point.

export function analyze(pf: ParsedFile, bytes: Uint8Array): CodecAnalysis | null {
  if (pf.format !== 'ogg') return null;
  const codec = pf.audio.codec;
  if (codec !== 'Opus' && codec !== 'Vorbis' && codec !== 'FLAC') return null;
  const a = collect(pf, bytes);
  if (!a || !a.packets.length) return null;
  if (codec === 'Opus') return opusAnalysis(bytes, a);
  if (codec === 'Vorbis') return vorbisAnalysis(bytes, a, vorbisSetup(bytes, a));
  const cache = new Map<number, Uint8Array>();
  return flacAnalysis({
    codec: 'FLAC (in Ogg)',
    si: streamInfoFrom(pf),
    units: a.packets.length,
    unitBytes: u => {
      const pk = a.packets[u];
      if (pk.pieces.length === 1) return bytes.subarray(pk.pieces[0][0], pk.pieces[0][1]);
      let hit = cache.get(u);
      if (!hit) cache.set(u, (hit = packetBytes(bytes, pk)));
      return hit;
    },
    unitTime: u => a.times[u],
    unitOf: i => { const u = unitAt(a, i); return u >= 0 && !a.packets[u].lost ? u : -1; },
  });
}
